import { useState, useEffect } from "react";
import { NavLink, useLocation, matchPath, useNavigate } from "react-router-dom";
import { FiLogOut, FiMenu, FiX } from "react-icons/fi";
import { FaHistory, FaShoppingBag, FaChartPie } from "react-icons/fa";
import { RiRefund2Fill, RiHandCoinFill } from "react-icons/ri";
import { MdCalendarToday } from "react-icons/md";
import { BsFillFuelPumpFill, BsBoxSeam } from "react-icons/bs";
import { MdAccountBox, MdPayments } from "react-icons/md";
import { TbSettingsDollar } from "react-icons/tb";
import { GiTakeMyMoney } from "react-icons/gi";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import logo from "../assets/FareFuel_logo.png";

const CustTopBar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [pickupOpen, setPickupOpen] = useState(false);
  const [investOpen, setInvestOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handleMobileClose = () => isMobile && setIsSidebarOpen(false);

  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/login");
  };

  // Active path for pickup history
  const isPickupHistoryActive =
    matchPath({ path: "/pickup-history/*" }, location.pathname) ||
    matchPath({ path: "/view-pickup/:pickup_id/*" }, location.pathname) ||
    matchPath({ path: "/scan-pickup/:pickup_id/*" }, location.pathname);

  // Active path for portfolio
  const isPortfolioActive =
    matchPath({ path: "/view-portfolio/*" }, location.pathname) ||
    location.pathname === "/view-portfolio";

  useEffect(() => {
    const handleResize = () => {
      const isNowMobile = window.innerWidth <= 1024;
      setIsMobile(isNowMobile);
      setIsSidebarOpen(!isNowMobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const linkClass =
    "flex items-center gap-2 py-2 px-3 text-lg rounded-md font-semibold leading-8 transition-colors";
  const activeClass = "bg-yellow-400 text-black";
  const hoverClass = "hover:bg-yellow-200";
  const iconClass = "text-blue-600";

  return (
    <>
      {/* Header */}
      <header
        className={`fixed top-0 ${isMobile ? "left-0" : "left-64"
          } right-0 h-16 bg-white shadow z-40 px-4 flex items-center justify-between transition-all duration-300`}
      >
        <div className="flex items-center gap-4">
          {isMobile && (
            <button onClick={toggleSidebar} className="p-2 rounded hover:bg-gray-100" >
              {isSidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          )}
          <h1 className="text-lg font-semibold whitespace-nowrap"> Welcome Customer!! </h1>
        </div>

        <div className="flex items-center gap-4">
          <button onClick={handleLogout} className="p-2 rounded-full text-red-500 hover:text-red-700 hover:bg-gray-100"
            title="Logout" >
            <FiLogOut size={20} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 w-64 h-screen z-50 
    bg-lime-300 bg-gradient-to-b from-lime-400 to-lime-100 
    text-green-900 shadow-lg  transition-transform duration-300 ease-in-out 
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}  xl:translate-x-0  overflow-y-auto scrollbar-hide`} >
        <div className="xl:hidden flex justify-end p-4">
          <button onClick={toggleSidebar} className="text-green-900 hover:text-black" title="Close Sidebar">
            <FiX size={28} />
          </button>
        </div>

        <div className="flex flex-col h-full">
          <div className="p-4 flex items-center justify-center space-x-2">
            <img src={logo} alt="FareFuel" className="w-40" />
          </div>

          <nav className="flex-1 px-4 space-y-2 text-[16px] font-medium">
            {/* My Account */}
            <NavLink to="/account" onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              } >
              <MdAccountBox size={20} className={iconClass} /> My Account
            </NavLink>

            {/* Fuel Wallet (Can be uncomment later) */}
            {/* <NavLink to="/FuelWallet"  onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              } >
              <BsFillFuelPumpFill className={iconClass} /> Fuel Credit Wallet
            </NavLink> */}

            {/* Pending Funds */}
            <NavLink to="/customer/pendingfund" onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              } >
              <RiRefund2Fill className={iconClass} /> Pending Funds
            </NavLink>

            {/* Request Pickup with Dropdown */}
            <div>
              <button onClick={() => { navigate("/request-pickup");
                  setPickupOpen(true); handleMobileClose(); }}
                className={`${linkClass} w-full justify-between ${location.pathname.startsWith("/pickup-history") || isPickupHistoryActive || location.pathname === "/request-pickup"
                  ? activeClass : hoverClass }`} >
                <span className="flex items-center gap-2"> <BsBoxSeam className={iconClass} /> Request Pickup </span>
                <span>
                  {pickupOpen ? (
                    <FiChevronUp className="w-4 h-4" /> ) : ( <FiChevronDown className="w-4 h-4" />
                  )}
                </span>
              </button>

              {pickupOpen && (
                <div className="ml-6 mt-1 space-y-1">
                  <NavLink to="/pickup-history"  onClick={handleMobileClose}
                    className={({ isActive }) =>
                      `${linkClass} text-sm ${isActive ? activeClass : hoverClass}`
                    }>
                    <FaHistory className={iconClass} /> Pickup History
                  </NavLink>
                  <NavLink to="/customer-schedule" onClick={handleMobileClose}
                    className={({ isActive }) =>
                      `${linkClass} text-sm ${isActive ? activeClass : hoverClass}`
                    }>
                    <MdCalendarToday className={iconClass} /> Calendar view
                  </NavLink>
                </div>
              )}
            </div>

            {/* Request Bag */}
            <NavLink to="/request-bag" onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              }>
              <FaShoppingBag className={iconClass} /> Request Bag
            </NavLink>

            {/* Payment History */}
            <NavLink to="/customer/paymentfund" onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              }>
              <MdPayments className={iconClass} /> Payment History
            </NavLink>

            <NavLink
              to="" onClick={handleMobileClose} className={`${linkClass} ${hoverClass}`}
              end >
              <TbSettingsDollar className={iconClass} /> Payment Settings
            </NavLink>

            {/* Invest Credits with Dropdown (Can be uncomment later) */}
            {/* <div>
              <button  onClick={() => setInvestOpen(!investOpen)}
                className={`${linkClass} w-full justify-between ${location.pathname.startsWith("/invest-credits") || isPortfolioActive
                  ? activeClass : hoverClass}`} >
                <span className="flex items-center gap-2">
                  <GiTakeMyMoney className={iconClass} /> Invest Credits
                </span>
                <span>
                  {investOpen ? (
                    <FiChevronUp className="w-4 h-4" /> ) : ( <FiChevronDown className="w-4 h-4" />
                  )}
                </span>
              </button>

              {investOpen && (
                <div className="ml-6 mt-1 space-y-1">
                  <NavLink to="/invest-credits" onClick={handleMobileClose}
                    className={({ isActive }) =>
                      `${linkClass} text-sm ${isActive ? activeClass : hoverClass}`
                    }>
                    <RiHandCoinFill className={iconClass} /> Invest Now
                  </NavLink>
                  <NavLink to="/view-portfolio" onClick={handleMobileClose}
                    className={({ isActive }) =>
                      `${linkClass} text-sm ${isActive ? activeClass : hoverClass}`
                    }>
                    <FaChartPie className={iconClass} /> Portfolio
                  </NavLink>
                </div>
              )}
            </div> */}

            <NavLink to="/customer/invest-now" onClick={handleMobileClose}
              className={({ isActive }) =>
                `${linkClass} ${isActive ? activeClass : hoverClass}`
              }>
              <RiHandCoinFill className={iconClass} /> Invest Now
            </NavLink>
          </nav>
        </div>
      </aside>

      {/* Overlay */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={toggleSidebar}
        ></div>
      )}
    </>
  );
};

export default CustTopBar;
